import cx from "classnames";
import { useContext, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../redux/store";
import IconButton from "../IconButton/IconButton";
import Button, { ButtonType } from "../Button/Button";
import AgreementLinks from "./AgreementLinks/AgreementLinks";
import { Context, IContext } from "../../context/ContextApp";
import Icon from "../Icon/icon";
import { subdomainTypes } from "../../context/availableSubdomains";
import SublimationMenu from "./SublimationMenu/SublimationMenu";
import SidebarSection from "./SidebarSection";
import GeneralMenu from "./GeneralMenu/GeneralMenu";
import { resourcesLinks } from "./GeneralMenu/data";

interface Props {
  collapseSidebar: () => void;
  isSidebarCollapsed: boolean;
  email: string;
  onLogOut: () => void;
}

const Sidebar = ({ isSidebarCollapsed, collapseSidebar, email, onLogOut }: Props) => {
  const navigate = useNavigate();
  const jwt = localStorage.getItem("jwt") || "";
  const isEnableSublimation = process.env.REACT_APP_ENABLE_SUBLIMATION === "true";
  const [expandedGroup, setExpandedGroup] = useState("");
  const organization = useSelector((state: RootState) => state.organization);
  const { appBranch }: IContext = useContext(Context);

  const renderSelectedOfficeNameAndChangeButton = () => {
    if (organization.is_webstore_admin && organization.selected_office != null) {
      return (
        <div className="sidebar-office-card">
          <p className="sidebar-office-card__label">Current Selected Office</p>
          <p className="sidebar-office-card__office">{organization.selected_office.company_name}</p>
          <Button type={ButtonType.secondary} onClick={() => navigate("/branch-selection")}>
            Change Office
          </Button>
        </div>
      );
    }
  };

  return (
    <>
      <div
        className={cx({
          "sidebar-wrapper": true,
          collapsed: isSidebarCollapsed,
        })}
        aria-hidden={isSidebarCollapsed}
      >
        <aside
          className={cx({
            sidebar: true,
            collapsed: isSidebarCollapsed,
          })}
          role="dialog"
          aria-label="Webstore menu"
        >
          <header className="sidebar__header">
            <div>
              <p className="sidebar__title">Webstore</p>
              <h2 className="sidebar__subtitle">Navigation</h2>
              <a
                href={process.env[`REACT_APP_LEGACY_WEBSTORE_URL_${appBranch.toUpperCase()}`]}
                className="sidebar__legacy-link"
                target="_blank"
                rel="noreferrer"
              >
                Return to Previous Version
                <Icon name="ExternalLink" iconWidth="0.85rem" />
              </a>
            </div>
            <IconButton inline onClick={collapseSidebar} aria-label="Close sidebar menu">
              <Icon name="TimesWhite" iconWidth="1.5rem" />
            </IconButton>
          </header>

          <div className="sidebar__body">
            <div className="sidebar__account">
              <p className="sidebar__account-welcome">User</p>
              <div className="sidebar__account-row">
                <span className="sidebar__account-email">{email}</span>
                <button className="sidebar__account-logout" onClick={onLogOut}>
                  <Icon name="SignOutAlt" iconWidth="1rem" />
                  <span>Log Out</span>
                </button>
              </div>
            </div>

            {renderSelectedOfficeNameAndChangeButton()}

            {/* Webstore / Resources / Brands */}
            <GeneralMenu appBranch={appBranch} />

            {isEnableSublimation && appBranch !== subdomainTypes.CA && (
              <SidebarSection title="YOUR DESIGN. OUR CANVAS.">
                <SublimationMenu
                  jwtToken={jwt}
                  expandedGroup={expandedGroup}
                  setExpandedGroup={setExpandedGroup}
                  name={"Biz Co.Lab"}
                />
              </SidebarSection>
            )}

            <GeneralMenu appBranch={appBranch} sections={[resourcesLinks]} />

            <SidebarSection title="Compliance">
              <AgreementLinks branch={appBranch} />
            </SidebarSection>
          </div>
        </aside>
        <div className="empty-space" onClick={collapseSidebar} />
      </div>
    </>
  );
};

export default Sidebar;
